import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

interface N {
  id: string;
  kind: string;
  title: string;
  body: string;
  conversationId: string | null;
  url?: string;
  read: boolean;
  createdAt: number;
}

/** In-app notifications for runs that finished while you were away (+ a desktop notification if you allow it). */
export function NotificationBell({ onNavigate }: { onNavigate: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api<{ unread: number; notifications: N[] }>("/notifications"),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const read = useMutation({
    mutationFn: (ids?: string[]) => api("/notifications/read", { method: "POST", body: { ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const clear = useMutation({
    mutationFn: () => api("/notifications", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const list = q.data?.notifications ?? [];
    if (seen.current === null) {
      seen.current = new Set(list.map((n) => n.id));
      return;
    }
    for (const n of list) {
      if (!seen.current.has(n.id) && !n.read) {
        seen.current.add(n.id);
        if (
          document.hidden &&
          typeof Notification !== "undefined" &&
          Notification.permission === "granted"
        )
          new Notification(n.title, { body: n.body, icon: "/icon-192.png" });
      }
    }
  }, [q.data]);
  const unread = q.data?.unread ?? 0;
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          aria-label={t("notif.title")}
          className="relative grid size-8 place-items-center rounded-lg text-[var(--fg-muted)] hover:bg-[var(--surface-3)]"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -end-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold leading-4 text-[var(--accent-fg)]">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={6}
          className="glass z-50 max-h-[70dvh] w-80 overflow-y-auto rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)]"
        >
          <div className="flex items-center justify-between px-2.5 py-1.5 text-xs text-[var(--fg-muted)]">
            <span className="font-medium">{t("notif.title")}</span>
            <span className="flex gap-3">
              {typeof Notification !== "undefined" && Notification.permission === "default" && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => void Notification.requestPermission()}
                >
                  {t("notif.enableDesktop")}
                </button>
              )}
              {unread > 0 && (
                <button type="button" className="underline" onClick={() => read.mutate(undefined)}>
                  {t("notif.markRead")}
                </button>
              )}
              {(q.data?.notifications.length ?? 0) > 0 && (
                <button type="button" className="underline" onClick={() => clear.mutate()}>
                  {t("notif.clear")}
                </button>
              )}
            </span>
          </div>
          {!q.data?.notifications.length && (
            <p className="px-3 py-6 text-center text-sm text-[var(--fg-faint)]">
              {t("notif.none")}
            </p>
          )}
          {q.data?.notifications.map((n) => (
            <Dropdown.Item
              key={n.id}
              onSelect={() => {
                read.mutate([n.id]);
                const to = n.url ?? (n.conversationId ? `/c/${n.conversationId}` : "/");
                if (to !== "/") {
                  navigate(to);
                  onNavigate();
                }
              }}
              className="flex cursor-pointer gap-2 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)]"
            >
              <span
                className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-[var(--accent)]"}`}
              />
              <span className="min-w-0">
                <span className="block truncate font-medium" dir="auto">
                  {n.title}
                </span>
                {n.body && (
                  <span className="line-clamp-2 text-xs text-[var(--fg-muted)]" dir="auto">
                    {n.body}
                  </span>
                )}
                <span className="text-[10px] text-[var(--fg-faint)]">
                  {new Date(n.createdAt).toLocaleString()}
                </span>
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
